import {
	AddressLink,
	CustomerClient,
	CustomerLink,
	Division,
	FieldWorkerLink,
	JobLink,
	JobsClient,
	RailroadLink,
} from 'src/shared/api';
import { Observable, merge } from 'rxjs';
import {
	debounceTime,
	filter,
	map,
	mapTo,
	shareReplay,
	startWith,
	switchMap,
} from 'rxjs/operators';
import {
	ensureValidTypeAheadInput,
	grabValidIdFromObject,
} from 'src/shared/utilities/custom-rxjs-operators';

import { Injectable } from '@angular/core';
import { isIdentifiableObject } from 'src/shared/utilities/utility-functions';

@Injectable({
	providedIn: 'root',
})
export class TypeAheadService {
	constructor(
		private readonly customerClient: CustomerClient,
		private readonly jobsClient: JobsClient
	) {}

	filteredRailroads$(
		stream$: Observable<string | RailroadLink>
	): Observable<RailroadLink[]> {
		const emptyRailroadsTypeAhead$ = stream$.pipe(
			filter<{ id?: number } | string, { id?: number }>(
				isIdentifiableObject
			),
			mapTo([])
		);

		const filteredRailroadsToShow$ = stream$.pipe(
			startWith(''),
			ensureValidTypeAheadInput(),
			switchMap((s) => this.customerClient.getRailroadPickList(s))
		);

		return merge(emptyRailroadsTypeAhead$, filteredRailroadsToShow$).pipe(
			shareReplay(1)
		);
	}

	divisionsForRailroad$(
		stream$: Observable<{ railroad?: string | RailroadLink }>
	): Observable<Division[]> {
		return stream$.pipe(
			map(
				(value: { railroad?: string | RailroadLink }) => value.railroad
			),
			grabValidIdFromObject(),
			switchMap((id) => this.customerClient.getRailroad(id)),
			map((rr) => rr.divisions),
			shareReplay(1)
		);
	}

	offeredServices$(
		stream$: Observable<{ railroad?: string | RailroadLink }>
	): Observable<Division[]> {
		return stream$.pipe(
			map(
				(value: { railroad?: string | RailroadLink }) => value.railroad
			),
			grabValidIdFromObject(),
			switchMap((id) => this.customerClient.getRailroad(id)),
			map((rr) => rr.offeredServices),
			shareReplay(1)
		);
	}

	railroadSubcodes$(
		stream$: Observable<{ railroad?: string | RailroadLink }>
	): Observable<Division[]> {
		return stream$.pipe(
			map(
				(value: { railroad?: string | RailroadLink }) => value.railroad
			),
			grabValidIdFromObject(),
			switchMap((id) => this.customerClient.getRailroad(id)),
			map((rr) => rr.subCodes),
			shareReplay(1)
		);
	}

	filteredCustomers$(
		stream$: Observable<string | CustomerLink>
	): Observable<CustomerLink[]> {
		const emptyCustomersTypeAhead$ = stream$.pipe(
			filter<{ id?: number } | string, { id?: number }>(
				isIdentifiableObject
			),
			mapTo([])
		);

		const filteredCustomersToShow$ = stream$.pipe(
			startWith(''),
			ensureValidTypeAheadInput(),
			switchMap((s) => this.customerClient.getCustomerPickList(s))
		);

		return merge(emptyCustomersTypeAhead$, filteredCustomersToShow$).pipe(
			shareReplay(1)
		);
	}

	filteredJobs$(
		stream$: Observable<string | JobLink>
	): Observable<JobLink[]> {
		const emptyJobsTypeAhead$ = stream$.pipe(
			filter<{ id?: number } | string, { id?: number }>(
				isIdentifiableObject
			),
			mapTo([])
		);

		const filteredJobsToShow$ = stream$.pipe(
			ensureValidTypeAheadInput(),
			switchMap((s) => this.jobsClient.getJobsPickList(s))
		);

		return merge(emptyJobsTypeAhead$, filteredJobsToShow$).pipe(
			shareReplay(1)
		);
	}

	filteredFieldWorkers$(
		stream$: Observable<string | FieldWorkerLink>
	): Observable<FieldWorkerLink[]> {
		const emptyFieldWorkersTypeAhead$ = stream$.pipe(
			filter<{ id?: number } | string, { id?: number }>(
				isIdentifiableObject
			),
			mapTo([])
		);

		const filteredFieldWorkersToShow$ = stream$.pipe(
			ensureValidTypeAheadInput(),
			debounceTime(250),
			switchMap((s) => this.customerClient.getFieldWorkersPickList(s))
		);

		return merge(
			emptyFieldWorkersTypeAhead$,
			filteredFieldWorkersToShow$
		).pipe(shareReplay(1));
	}

	filteredAddresses$(
		stream$: Observable<string | AddressLink>
	): Observable<AddressLink[]> {
		const emptyAddressesTypeAhead$ = stream$.pipe(
			filter(isIdentifiableObject),
			mapTo([])
		);

		const filteredAddressesToShow$ = stream$.pipe(
			ensureValidTypeAheadInput(),
			switchMap((s) => this.jobsClient.getLocationsPickList(s))
		);

		return merge(emptyAddressesTypeAhead$, filteredAddressesToShow$).pipe(
			shareReplay(1)
		);
	}
}
